import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import './NotificationBell.css'

export default function NotificationBell() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState(null) // null = not loaded yet
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)

  useEffect(() => {
    if (!user) return
    fetchUnreadCount()
    // light polling so the badge updates without a full page reload
    const interval = setInterval(fetchUnreadCount, 60000)
    return () => clearInterval(interval)
  }, [user]) // eslint-disable-line

  useEffect(() => {
    function handleClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function fetchUnreadCount() {
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
    setUnreadCount(count || 0)
  }

  async function toggleOpen() {
    const opening = !open
    setOpen(opening)
    if (opening && items === null) {
      setLoading(true)
      const { data } = await supabase
        .from('notifications')
        .select('*, profiles!notifications_actor_id_fkey(display_name, full_name, username, avatar_url), projects!notifications_project_id_fkey(title)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20)
      setItems(data || [])
      setLoading(false)
    }
  }

  async function handleItemClick(n) {
    if (!n.is_read) {
      await supabase.from('notifications').update({ is_read: true }).eq('id', n.id)
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x))
      setUnreadCount(c => Math.max(0, c - 1))
    }
    setOpen(false)
    if (n.project_id) navigate(`/project/${n.project_id}`)
  }

  async function markAllRead() {
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false)
    setItems(prev => (prev || []).map(x => ({ ...x, is_read: true })))
    setUnreadCount(0)
  }

  if (!user) return null

  return (
    <div className="notif-bell-wrap" ref={boxRef}>
      <button className="notif-bell-btn" onClick={toggleOpen} aria-label="Notifications">
        <BellIcon />
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-head">
            <span>Notifications</span>
            {unreadCount > 0 && <button className="notif-mark-all" onClick={markAllRead}>Mark all read</button>}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><span className="loading-spinner" /></div>
          ) : (items || []).length === 0 ? (
            <div className="notif-empty">Nothing yet — likes and comments on your commits will show up here.</div>
          ) : (
            <div className="notif-list">
              {items.map(n => {
                const actorName = n.profiles?.display_name || n.profiles?.full_name || 'Someone'
                const projectTitle = n.projects?.title || 'your project'
                return (
                  <button key={n.id} className={`notif-item ${n.is_read ? '' : 'notif-item-unread'}`} onClick={() => handleItemClick(n)}>
                    <div className="notif-item-avatar">
                      {n.profiles?.avatar_url
                        ? <img src={n.profiles.avatar_url} alt={actorName} />
                        : <span>{actorName.charAt(0).toUpperCase()}</span>}
                    </div>
                    <div className="notif-item-body">
                      {n.type === 'like' ? (
                        <span>
                          <strong>{actorName}</strong>
                          {n.count > 1 ? ` and ${n.count - 1} other${n.count - 1 > 1 ? 's' : ''}` : ''} liked your commit in <em>{projectTitle}</em>
                        </span>
                      ) : n.type === 'comment_like' ? (
                        <span>
                          <strong>{actorName}</strong>
                          {n.count > 1 ? ` and ${n.count - 1} other${n.count - 1 > 1 ? 's' : ''}` : ''} liked your comment in <em>{projectTitle}</em>
                        </span>
                      ) : n.type === 'reply' ? (
                        <span>
                          <strong>{actorName}</strong> replied to your comment in <em>{projectTitle}</em>
                          {n.preview && <div className="notif-preview">"{n.preview}"</div>}
                        </span>
                      ) : (
                        <span>
                          <strong>{actorName}</strong> commented on your commit in <em>{projectTitle}</em>
                          {n.preview && <div className="notif-preview">"{n.preview}"</div>}
                        </span>
                      )}
                      <span className="notif-time">{timeAgo(n.created_at)}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
